import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { GfFlyerItemDto, Coordinate } from "@swagger/typescript-fetch-goflyer";
import { CoordinateChange } from "../types";
import {
  MODAL_CONSTANTS,
  SAVE_CONFIRMATION_MODAL_CONSTANTS,
  VIEWER_COLORS,
} from "../constants";

interface SaveConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  changes: CoordinateChange[];
  deletedItems: GfFlyerItemDto[];
  isSaving: boolean;
}

interface CategorizedChanges {
  created: CoordinateChange[];
  updated: CoordinateChange[];
  deleted: GfFlyerItemDto[];
}

/**
 * Modal dialog for reviewing and confirming coordinate changes before saving.
 *
 * This component displays a categorized list of all pending changes including:
 * - New coordinates being created
 * - Existing coordinates being modified (position/size changes)
 * - Coordinates being deleted/removed
 *
 * Users can review all changes in detail before confirming to save to the database.
 * The modal prevents closing while a save operation is in progress and displays
 * the total number of changes in the title and confirmation button.
 *
 * @param props - Component properties
 * @param props.open - Controls whether the modal is visible
 * @param props.onClose - Callback invoked when the modal should close (disabled during save)
 * @param props.onConfirm - Callback invoked when user confirms and wants to save all changes
 * @param props.changes - Array of coordinate changes including creates and updates
 * @param props.deletedItems - Array of flyer items whose coordinates will be removed
 * @param props.isSaving - Whether a save operation is currently in progress
 */
export const SaveConfirmationModal: React.FC<SaveConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  changes,
  deletedItems,
  isSaving,
}) => {
  /**
   * Categorizes changes into created, updated, and deleted groups.
   *
   * Changes without an original coordinate are considered new creations,
   * while changes with an original coordinate are updates. Deleted items
   * are passed separately and included as-is.
   *
   * @returns Object containing arrays of created, updated, and deleted changes
   */
  const categorized = React.useMemo((): CategorizedChanges => {
    const created: CoordinateChange[] = [];
    const updated: CoordinateChange[] = [];

    changes.forEach((change) => {
      if (!change.originalCoordinate) {
        created.push(change);
      } else {
        updated.push(change);
      }
    });

    return {
      created,
      updated,
      deleted: deletedItems,
    };
  }, [changes, deletedItems]);

  const totalChanges =
    categorized.created.length +
    categorized.updated.length +
    categorized.deleted.length;

  /**
   * Renders a single change item in the confirmation list.
   *
   * Displays the item name, Chinese name (if available), category, page number,
   * and an action chip indicating the type of change. Each item is color-coded
   * based on the change type (green for created, orange for updated, red for deleted).
   *
   * @param item - The flyer item being changed
   * @param type - Type of change: "created", "updated", or "deleted"
   * @param coordinate - The coordinate data associated with the change (optional)
   * @returns JSX element representing the change item in the list
   */
  const renderChangeItem = (
    item: GfFlyerItemDto,
    type: "created" | "updated" | "deleted",
    coordinate?: Coordinate,
  ) => {
    const icon =
      type === "created" ? (
        <AddCircleIcon sx={{ color: VIEWER_COLORS.SUCCESS_GREEN }} />
      ) : type === "updated" ? (
        <EditIcon sx={{ color: VIEWER_COLORS.PRIMARY_ORANGE }} />
      ) : (
        <DeleteIcon sx={{ color: VIEWER_COLORS.ERROR_RED }} />
      );

    const chipColor =
      type === "created" ? "success" : type === "updated" ? "warning" : "error";

    const actionText =
      type === "created"
        ? "New Coordinate"
        : type === "updated"
          ? "Modified"
          : "Will be Removed";

    return (
      <ListItem
        key={item.id}
        sx={{
          borderLeft: `${SAVE_CONFIRMATION_MODAL_CONSTANTS.LIST_ITEM_BORDER_WIDTH}px solid ${
            type === "created"
              ? VIEWER_COLORS.SUCCESS_GREEN
              : type === "updated"
                ? VIEWER_COLORS.PRIMARY_ORANGE
                : VIEWER_COLORS.ERROR_RED
          }`,
          mb: SAVE_CONFIRMATION_MODAL_CONSTANTS.LIST_ITEM_MARGIN_BOTTOM,
          backgroundColor: VIEWER_COLORS.LIST_ITEM_BG,
          borderRadius:
            SAVE_CONFIRMATION_MODAL_CONSTANTS.LIST_ITEM_BORDER_RADIUS,
        }}
      >
        <Box sx={{ mr: SAVE_CONFIRMATION_MODAL_CONSTANTS.ICON_MARGIN_RIGHT }}>
          {icon}
        </Box>
        <ListItemText
          primary={
            <Box
              display="flex"
              alignItems="center"
              gap={SAVE_CONFIRMATION_MODAL_CONSTANTS.TITLE_GAP}
            >
              <Typography variant="body1" fontWeight="medium">
                {item.name || "Untitled"}
              </Typography>
              {item.nameChinese && (
                <Typography variant="body2" color="text.secondary">
                  ({item.nameChinese})
                </Typography>
              )}
            </Box>
          }
          secondary={
            <Box
              display="flex"
              gap={SAVE_CONFIRMATION_MODAL_CONSTANTS.CHIP_GAP}
              mt={SAVE_CONFIRMATION_MODAL_CONSTANTS.CHIP_MARGIN_TOP}
              alignItems="center"
            >
              <Chip
                label={actionText}
                size="small"
                color={chipColor}
                sx={{
                  height: MODAL_CONSTANTS.CHIP_HEIGHT,
                  fontSize: MODAL_CONSTANTS.CHIP_FONT_SIZE,
                }}
              />
              {coordinate && coordinate.flyerPage !== undefined && (
                <Chip
                  label={`Page ${coordinate.flyerPage}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: MODAL_CONSTANTS.CHIP_HEIGHT,
                    fontSize: MODAL_CONSTANTS.CHIP_FONT_SIZE,
                  }}
                />
              )}
              {item.gfFlyerItemCategory?.[0] && (
                <Chip
                  label={item.gfFlyerItemCategory[0].name}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: MODAL_CONSTANTS.CHIP_HEIGHT,
                    fontSize: MODAL_CONSTANTS.CHIP_FONT_SIZE,
                  }}
                />
              )}
            </Box>
          }
          secondaryTypographyProps={{ component: "div" }}
        />
      </ListItem>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: MODAL_CONSTANTS.PAPER_HEIGHT,
          maxHeight: MODAL_CONSTANTS.PAPER_MAX_HEIGHT,
        },
      }}
      sx={{
        zIndex: MODAL_CONSTANTS.Z_INDEX,
      }}
      data-testid="flyer-coordinate-save-confirm-dialog"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Confirm Save Changes ({totalChanges})
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            disabled={isSaving}
            data-testid="flyer-coordinate-save-confirm-close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography
          variant="body2"
          color="text.secondary"
          mb={SAVE_CONFIRMATION_MODAL_CONSTANTS.DESCRIPTION_MARGIN_BOTTOM}
        >
          Review the changes before saving. This action will update the
          database.
        </Typography>

        {categorized.created.length > 0 && (
          <Box mb={SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_MARGIN_BOTTOM}>
            <Typography
              variant="subtitle2"
              sx={{ color: VIEWER_COLORS.SUCCESS_GREEN }}
              fontWeight="bold"
              mb={SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_TITLE_MARGIN_BOTTOM}
              display="flex"
              alignItems="center"
              gap={SAVE_CONFIRMATION_MODAL_CONSTANTS.TITLE_ICON_GAP}
            >
              <AddCircleIcon fontSize="small" />
              New Coordinates ({categorized.created.length})
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              mb={
                SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_CAPTION_MARGIN_BOTTOM
              }
              display="block"
            >
              These items will have new coordinates added
            </Typography>
            <List disablePadding dense>
              {categorized.created.map((change) =>
                renderChangeItem(
                  change.flyerItem,
                  "created",
                  change.modifiedCoordinate,
                ),
              )}
            </List>
            <Divider
              sx={{
                mt: SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_DIVIDER_MARGIN_TOP,
              }}
            />
          </Box>
        )}

        {categorized.updated.length > 0 && (
          <Box mb={SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_MARGIN_BOTTOM}>
            <Typography
              variant="subtitle2"
              sx={{ color: VIEWER_COLORS.PRIMARY_ORANGE }}
              fontWeight="bold"
              mb={SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_TITLE_MARGIN_BOTTOM}
              display="flex"
              alignItems="center"
              gap={SAVE_CONFIRMATION_MODAL_CONSTANTS.TITLE_ICON_GAP}
            >
              <EditIcon fontSize="small" />
              Modified Coordinates ({categorized.updated.length})
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              mb={
                SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_CAPTION_MARGIN_BOTTOM
              }
              display="block"
            >
              These items have coordinate changes (position or size)
            </Typography>
            <List disablePadding dense>
              {categorized.updated.map((change) =>
                renderChangeItem(
                  change.flyerItem,
                  "updated",
                  change.modifiedCoordinate,
                ),
              )}
            </List>
            <Divider
              sx={{
                mt: SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_DIVIDER_MARGIN_TOP,
              }}
            />
          </Box>
        )}

        {categorized.deleted.length > 0 && (
          <Box mb={SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_MARGIN_BOTTOM}>
            <Typography
              variant="subtitle2"
              sx={{ color: VIEWER_COLORS.ERROR_RED }}
              fontWeight="bold"
              mb={SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_TITLE_MARGIN_BOTTOM}
              display="flex"
              alignItems="center"
              gap={SAVE_CONFIRMATION_MODAL_CONSTANTS.TITLE_ICON_GAP}
            >
              <DeleteIcon fontSize="small" />
              Removed Coordinates ({categorized.deleted.length})
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              mb={
                SAVE_CONFIRMATION_MODAL_CONSTANTS.SECTION_CAPTION_MARGIN_BOTTOM
              }
              display="block"
            >
              These items will have their coordinates removed
            </Typography>
            <List disablePadding dense>
              {categorized.deleted.map((item) =>
                renderChangeItem(item, "deleted", item.coordinate),
              )}
            </List>
          </Box>
        )}

        {totalChanges === 0 && (
          <Box
            textAlign="center"
            py={SAVE_CONFIRMATION_MODAL_CONSTANTS.EMPTY_STATE_PADDING_Y}
          >
            <Typography variant="body1" color="text.secondary">
              No changes to save
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: SAVE_CONFIRMATION_MODAL_CONSTANTS.ACTIONS_PADDING,
          justifyContent: SAVE_CONFIRMATION_MODAL_CONSTANTS.ACTIONS_JUSTIFY,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isSaving}
          color="error"
          data-testid="flyer-coordinate-save-confirm-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={totalChanges === 0 || isSaving}
          sx={{
            backgroundColor: VIEWER_COLORS.PRIMARY_ORANGE,
            "&:hover": {
              backgroundColor: VIEWER_COLORS.PRIMARY_ORANGE_HOVER,
            },
          }}
          data-testid="flyer-coordinate-save-confirm-submit"
        >
          {isSaving ? "Saving..." : `Confirm & Save (${totalChanges})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
